import type { ShiftMode } from '../drivetrain/VirtualDrivetrain';
import { cleanGearTops } from '../cars/iceCars';

export type TelemetrySource = 'sim' | 'gps' | 'can';
export type DriveStyleSetting = 'auto' | 'eco' | 'sport';

export interface Settings {
  volume: number;
  gearbox: ShiftMode;
  /** the engine changes gear when the driver's own car does, whenever an OBD dongle lets us see it */
  followMyGears: boolean;
  source: TelemetrySource;
  lastCar: string | null;
  /** relay room for the CAN bridge (see bridge/README.md) */
  relayRoom: string;
  /** Auto-gearbox character: eco / sport / auto (detected from how you drive). */
  driveStyle: DriveStyleSetting;
  /** Your Tesla's real top speed — the virtual gearbox is scaled so top gear peaks there. */
  vehicleTopSpeedKmh: number;
  /** Tesla model id from TESLA_MODELS (legacy; the Home selector now sets vehicleTopSpeedKmh directly → 'custom'). */
  vehicleModel: string;
  /** speed units on every dash */
  units: 'kmh' | 'mph';
  /** EV: the virtual gearbox is scaled to vehicleTopSpeedKmh. ICE: it shifts where YOUR car shifts (vehicleGearTopsKmh). */
  vehicleKind: 'ev' | 'ice';
  /** ICE: road speed at the limiter in each gear (km/h), from the car list or typed in */
  vehicleGearTopsKmh: number[] | null;
  /** ICE: id from ICE_CARS, or 'custom' */
  vehicleCar: string | null;
  /** GEAR RATIO: 'porsche' = the engine's own real ladder (default) · 'own' = follow the driver's car (EV top speed / ICE gears) */
  gearing: 'porsche' | 'own';
  /** UI language code ('en', 'es'…); null = follow the phone */
  lang: string | null;
  /** admin only: the SIM with the log / audio levels / telemetry pills (the plain SIM is for everyone) */
  simAdmin: boolean;
}

const KEY = 'roardrive:settings:v1';
const DEFAULTS: Settings = { volume: 0.8, gearbox: 'auto', followMyGears: true, source: 'sim', lastCar: null, relayRoom: 'roar', driveStyle: 'auto', vehicleTopSpeedKmh: 200, vehicleModel: 'custom', units: 'kmh', vehicleKind: 'ev', vehicleGearTopsKmh: null, vehicleCar: null, gearing: 'porsche', lang: null, simAdmin: false };

/** Home selector: your car's real top speed (scales the virtual gearbox so top gear peaks there). */
export const TOP_SPEED_OPTIONS_KMH = [200, 210, 220, 230, 240, 250, 260, 270, 280];   // minimum 200 (Marc); the list ends at the live car's real limit
export const KMH_PER_MPH = 1.609344;

/** Tesla line-up with real (limited) top speeds — scales the virtual gearbox. */
export const TESLA_MODELS: { id: string; name: string; topKmh: number }[] = [
  { id: 'm3-rwd', name: 'Model 3 RWD', topKmh: 201 },
  { id: 'm3-lr', name: 'Model 3 Long Range', topKmh: 201 },
  { id: 'm3-perf', name: 'Model 3 Performance', topKmh: 262 },
  { id: 'my-rwd', name: 'Model Y RWD', topKmh: 217 },
  { id: 'my-lr', name: 'Model Y Long Range', topKmh: 217 },
  { id: 'my-perf', name: 'Model Y Performance', topKmh: 250 },
  { id: 'ms', name: 'Model S', topKmh: 250 },
  { id: 'ms-plaid', name: 'Model S Plaid', topKmh: 322 },
  { id: 'mx', name: 'Model X', topKmh: 250 },
  { id: 'mx-plaid', name: 'Model X Plaid', topKmh: 262 },
  { id: 'ct', name: 'Cybertruck', topKmh: 180 },
  { id: 'other', name: 'Other EV (200 km/h)', topKmh: 200 },
];

/** `?room=xyz` in the URL overrides the stored relay room (handy in the car). */
export function roomFromUrl(): string | null {
  try {
    const m = /[?&]room=([A-Za-z0-9_-]{1,64})/.exec(location.search + location.hash);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULTS };
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Settings>) };
  } catch {
    return { ...DEFAULTS };
  }
}

export function saveSettings(patch: Partial<Settings>): Settings {
  const next = { ...loadSettings(), ...patch };
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable — keep in memory */
  }
  schedulePush(next);
  return next;
}

// ---- account sync: signed-in users get their settings back on any Tesla ----
const SYNC_KEYS: (keyof Settings)[] = ['volume', 'gearbox', 'followMyGears', 'source', 'driveStyle', 'vehicleTopSpeedKmh', 'vehicleModel', 'units', 'vehicleKind', 'vehicleGearTopsKmh', 'vehicleCar', 'gearing', 'lang'];
let pushTimer: ReturnType<typeof setTimeout> | null = null;
function schedulePush(next: Settings): void {
  let signedIn = false;
  let deviceId = '';
  try {
    signedIn = !!localStorage.getItem('roardrive:account:v1');
    deviceId = localStorage.getItem('roardrive:device:v1') ?? '';
  } catch {
    /* ignore */
  }
  if (!signedIn || !deviceId || typeof fetch !== 'function') return;
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    pushTimer = null;
    const settings: Partial<Settings> = {};
    for (const k of SYNC_KEYS) (settings as Record<string, unknown>)[k] = next[k];
    void fetch('/api/profile/settings', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ deviceId, settings }),
    }).catch(() => undefined);
  }, 1500);
}

/** Settings that came back from the account at sign-in → apply locally (only known keys). */
export function applyRemoteSettings(remote: unknown): Partial<Settings> {
  if (!remote || typeof remote !== 'object') return {};
  const r = remote as Record<string, unknown>;
  const patch: Partial<Settings> = {};
  if (typeof r.volume === 'number' && r.volume >= 0 && r.volume <= 1) patch.volume = r.volume;
  if (r.gearbox === 'auto' || r.gearbox === 'manual') patch.gearbox = r.gearbox;
  if (typeof r.followMyGears === 'boolean') patch.followMyGears = r.followMyGears;
  if (r.source === 'gps' || r.source === 'can' || r.source === 'sim') patch.source = r.source;
  if (r.driveStyle === 'auto' || r.driveStyle === 'eco' || r.driveStyle === 'sport') patch.driveStyle = r.driveStyle;
  if (typeof r.vehicleModel === 'string' && TESLA_MODELS.some((m) => m.id === r.vehicleModel)) {
    patch.vehicleModel = r.vehicleModel;
    patch.vehicleTopSpeedKmh = TESLA_MODELS.find((m) => m.id === r.vehicleModel)!.topKmh;
  }
  if (typeof r.vehicleTopSpeedKmh === 'number' && r.vehicleTopSpeedKmh >= 100 && r.vehicleTopSpeedKmh <= 400) { patch.vehicleTopSpeedKmh = r.vehicleTopSpeedKmh; patch.vehicleModel = 'custom'; }
  if (r.units === 'kmh' || r.units === 'mph') patch.units = r.units;
  if (r.vehicleKind === 'ev' || r.vehicleKind === 'ice') patch.vehicleKind = r.vehicleKind;
  if (r.gearing === 'porsche' || r.gearing === 'own') patch.gearing = r.gearing;
  if (typeof r.lang === 'string' || r.lang === null) patch.lang = r.lang as string | null;
  if (typeof r.simAdmin === 'boolean') patch.simAdmin = r.simAdmin;
  if (Array.isArray(r.vehicleGearTopsKmh)) { const g = cleanGearTops(r.vehicleGearTopsKmh); if (g) patch.vehicleGearTopsKmh = g; }
  if (typeof r.vehicleCar === 'string' && r.vehicleCar.length <= 64) patch.vehicleCar = r.vehicleCar;
  if (!Object.keys(patch).length) return {};
  const next = { ...loadSettings(), ...patch };
  try {
    localStorage.setItem(KEY, JSON.stringify(next)); // no push: it just came from the server
  } catch {
    /* ignore */
  }
  return patch;
}
